import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import api from '../services/api'
import * as FMT from '../services/formatos'
// El día de hoy de la clínica para donde no hay hook (utils/hoyClinica): se le dice la zona de la sesión al cargarla.
import { fijarZonaDeLaClinica } from '../utils/hoyClinica'
// t93 · y el país de la clínica, para el país que sale ya puesto en un teléfono nuevo (components/CampoTelefono).
import { fijarPaisTelefonoClinica } from '../utils/paisTelefonoClinica'
import { t as i18nT } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ════════════════════════════════════════════════════════════════════════════════════════════════
// LA CONFIGURACIÓN REGIONAL DE LA CLÍNICA, ATADA A LAS PANTALLAS
//
// `services/formatos.js` son funciones puras: hay que pasarles la configuración. Este contexto la
// carga UNA vez (GET /api/regional) y entrega los formateadores YA ATADOS, para que una pantalla
// escriba `f.fecha(cita.fecha)` y no `fecha(cita.fecha, regional)`.
//
// Eso no es comodidad, es la diferencia entre que el barrido de R2 salga bien o mal: son ~186 sitios
// con `'es-ES'` clavado y 167 con el euro. Si cada uno tuviera que acordarse de conseguir `regional`
// y pasarlo, unos cuantos se lo saltarían y esas pantallas seguirían mintiendo — con la diferencia
// de que ya nadie las miraría, porque "eso ya está hecho".
//
//     const { f } = useRegional()          // o:  const f = useFormatos()
//     <td>{f.fecha(cita.fecha)}</td>       // 31/03/2026  ·  2026-03-31  ·  03/31/2026
//     <td>{f.dinero(linea.importe)}</td>   // 1.234,56 €  ·  $1,234.56
//
// ── LO QUE ESTE CONTEXTO NO HACE ────────────────────────────────────────────────────────────────
// No traduce. R1 deja el mecanismo (`t`) y el castellano como único idioma; traducir es R3.
// No toca contabilidad: hay otra tanda trabajando ahí y se cablea cuando termine.
// ════════════════════════════════════════════════════════════════════════════════════════════════

// El valor con el que se pinta mientras no se sabe nada. Es el defecto de la migración (la tabla
// `clinicas`), así que una instalación recién creada ve lo mismo antes y después de responder el
// servidor y no hay parpadeo de formato.
const DEFECTO = Object.freeze({ ...FMT.REGIONAL_POR_DEFECTO })

// ── LA CACHÉ VA POR DOMINIO, NUNCA GLOBAL ───────────────────────────────────────────────────────
// Es el mismo error que ya se cometió con la marca de la clínica (ver hooks/useBranding.js): con una
// clave global, una tablet compartida o un navegador que ha visto dos clínicas arrastra la
// configuración de la anterior. Aquí eso significa pintar las fechas de una clínica canaria en hora
// peninsular, que es exactamente el fallo que esta tanda viene a corregir.
const CLAVE = () => 'regional_cache:' + (typeof location !== 'undefined' ? location.host : 'app')

let _memoria = null        // evita volver a preguntar entre montajes de la misma sesión
let _memoriaToken = null   // CON QUÉ sesión se resolvió lo que hay en memoria

// ── QUIÉN ESTÁ MIRANDO AHORA MISMO ──────────────────────────────────────────────────────────────
// Al guardar en Ajustes, el token no cambia: sin este aviso, el dueño elegiría "AAAA-MM-DD", leería
// "Guardado" y la aplicación seguiría pintando "31/03/2026" hasta que alguien pulsara F5. Ya pasó
// con el color de marca y es el fallo más tonto posible en algo cuyo único propósito es verse.
const _mirando = new Set()
let _version = 0
const _avisarATodos = () => { _version++; for (const avisar of _mirando) avisar() }

/** Tira la caché y hace que TODAS las pantallas vivas vuelvan a preguntar. Para después de un PUT. */
export function olvidarRegional() {
  _memoria = null
  _memoriaToken = null
  fijarZonaDeLaClinica(null)
  fijarPaisTelefonoClinica(null)
  try { localStorage.removeItem(CLAVE()) } catch { /* modo incógnito, almacenamiento lleno */ }
  _avisarATodos()
}

const _token = () => { try { return localStorage.getItem('token') || null } catch { return null } }

// ── SANEAR LO QUE LLEGA POR LA RED ──────────────────────────────────────────────────────────────
// La zona horaria acaba dentro de Intl y el resto dentro de textos que se pintan. `formatos.js`
// aguanta una zona desconocida sin romperse (cae a UTC), pero caer a UTC es pintar mal TODAS las
// horas de la clínica sin decir nada. Aquí se comprueba de verdad —probando a construir el
// formateador— y si no vale se usa el defecto, que al menos es una zona real de su mercado.
function _zonaValida(z) {
  if (typeof z !== 'string' || !z.trim()) return false
  try { new Intl.DateTimeFormat('en-US', { timeZone: z }); return true } catch { return false }
}

function _sanear(bruto) {
  const v = (bruto && typeof bruto === 'object') ? bruto : {}
  const texto = (x, porDefecto) => (typeof x === 'string' && x.trim() ? x.trim() : porDefecto)
  const zona = texto(v.zona_horaria, DEFECTO.zona_horaria)
  const primerDia = Number(v.primer_dia_semana)
  const decimal = v.separador_decimal === '.' ? '.' : (v.separador_decimal === ',' ? ',' : DEFECTO.separador_decimal)
  return {
    zona_horaria: _zonaValida(zona) ? zona : DEFECTO.zona_horaria,
    idioma: texto(v.idioma, DEFECTO.idioma),
    formato_fecha: texto(v.formato_fecha, DEFECTO.formato_fecha),
    formato_hora: texto(v.formato_hora, DEFECTO.formato_hora),
    moneda: texto(v.moneda, DEFECTO.moneda),
    separador_decimal: decimal,
    // El de miles es SIEMPRE el otro, igual que lo deriva el servidor (services/regional.js). Se
    // recalcula aquí en vez de fiarse de lo que llegue: si los dos fueran el mismo carácter,
    // '1.234.56' no lo entiende nadie.
    separador_miles: decimal === ',' ? '.' : ',',
    primer_dia_semana: Number.isInteger(primerDia) && primerDia >= 0 && primerDia <= 6 ? primerDia : DEFECTO.primer_dia_semana,
    // Informativos del servidor, para quien los quiera pintar (Ajustes avisa de la migración que
    // falta). No los usa el formateo, así que si no vienen no pasa nada.
    locale: typeof v.locale === 'string' ? v.locale : null,
    origen: typeof v.origen === 'string' ? v.origen : null,
    falta_migracion: !!v.falta_migracion,
    // t93 · el país de la clínica (clinicas.pais), en dos letras; si no llega o no vale, ninguno (se usa España).
    pais: typeof v.pais === 'string' && /^[A-Za-z]{2}$/.test(v.pais.trim()) ? v.pais.trim().toUpperCase() : null,
  }
}

// El servidor devuelve `avisos` como LISTA (services/regional.js: la migración que falta, la clínica
// que no aparece, un ajuste guardado que ya no vale). Se admite también `aviso` en singular por si la
// pieza C envuelve la respuesta de otra forma: que un detalle de envoltorio no decida si el usuario
// se entera de que su configuración no se está guardando.
function _aviso(cuerpo, data) {
  for (const fuente of [cuerpo, data]) {
    if (!fuente || typeof fuente !== 'object') continue
    if (Array.isArray(fuente.avisos) && fuente.avisos.length) return fuente.avisos.join(' ')
    if (typeof fuente.aviso === 'string' && fuente.aviso.trim()) return fuente.aviso.trim()
  }
  return null
}

// Lo último que se supo, para pintar sin parpadeo mientras responde el servidor.
function _deCache() {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE()) || 'null')
    return v ? _sanear(v) : DEFECTO
  } catch { return DEFECTO }
}

const RegionalContext = createContext(null)

// Los formateadores atados a una configuración. Fuera del componente para poder reutilizarlos en el
// valor de emergencia de `useRegional()`.
function _formateadores(regional) {
  return {
    fecha: (valor, opciones) => FMT.fecha(valor, regional, opciones),
    hora: (valor, opciones) => FMT.hora(valor, regional, opciones),
    fechaHora: (valor, opciones) => FMT.fechaHora(valor, regional, opciones),
    dinero: (valor, opciones) => FMT.dinero(valor, regional, opciones),
    numero: (valor, opciones) => FMT.numero(valor, regional, opciones),
    // 'YYYY-MM-DD' de hoy EN LA CLÍNICA. Es el sustituto de los `new Date().toISOString().slice(0,10)`
    // que hay repartidos por la app y que, en cuanto una clínica no esté en la zona del navegador,
    // dan el día equivocado durante unas horas cada noche.
    hoy: (instante) => FMT.hoy(regional, instante),
    ahora: (instante) => FMT.ahora(regional, instante),
    moneda: FMT.monedaInfo(regional.moneda),
  }
}

export function RegionalProvider({ children }) {
  // ── LO CARGADO VA JUNTO CON LA SESIÓN QUE LO CARGÓ ──────────────────────────────────────────
  // Guardar la configuración suelta tiene un fallo silencioso: al cerrar sesión y entrar OTRA
  // clínica (una tablet compartida, un gestor que lleva varias), el estado seguiría teniendo la
  // configuración de la anterior mientras llega la nueva, y durante ese rato se pintarían fechas y
  // horas de otra clínica sin que nada lo indique. Con el token dentro, una sesión que no es la que
  // cargó el dato no lo usa: se cae al valor cacheado de este dominio, que sí es de esta clínica.
  const [cargada, setCargada] = useState(() => (_memoria && _memoriaToken ? { token: _memoriaToken, cfg: _memoria } : null))
  // Lo último que se supo en ESTE dominio: se lee una sola vez y solo sirve para pintar sin
  // parpadeo mientras responde el servidor.
  const cache = useMemo(() => _deCache(), [])
  // Aviso del servidor (pieza C): sin la migración aplicada responde los valores por defecto y lo
  // dice, en vez de dar error. Toda la app depende de esto, así que se degrada, no se cae.
  const [aviso, setAviso] = useState(null)
  const [version, setVersion] = useState(_version)

  // El token como estado: al entrar cambia de null a algo y el efecto de abajo vuelve a correr. Sin
  // esto, quien llega a la app desde /login (que se navega, no se recarga) se quedaría con la
  // configuración por defecto toda la sesión.
  const token = _token()

  // La configuración se DERIVA, no se guarda dos veces. Sin sesión, la de por defecto: en la
  // pantalla de acceso todavía no se sabe de qué clínica se trata, y suponerlo sería volver a
  // decidir por la clínica, que es justo lo que esta tanda viene a quitar.
  const regional = !token ? DEFECTO : ((cargada && cargada.token === token) ? cargada.cfg : cache)

  // "Cargando" también se DEDUCE, no se guarda: hay sesión, todavía no hay dato de esta sesión y no
  // ha fallado nada. Guardarlo en un estado obligaba a llamar a setState dentro del efecto, que es
  // justo lo que provoca una segunda pintada de toda la aplicación en cada arranque.
  const cargando = !!token && !(cargada && cargada.token === token) && aviso === null

  useEffect(() => {
    const avisar = () => setVersion(_version)
    _mirando.add(avisar)
    return () => { _mirando.delete(avisar) }
  }, [])

  useEffect(() => {
    let vivo = true

    // ── SIN SESIÓN NO SE PREGUNTA, Y ESTO NO ES UNA OPTIMIZACIÓN ────────────────────────────────
    // GET /api/regional pide sesión. Llamarlo desde la pantalla de acceso devolvería 401, y el
    // interceptor de services/api.js trata cualquier 401 como "sesión muerta": limpia el
    // almacenamiento y manda a /login. Es decir, este contexto expulsaría al usuario que está
    // escribiendo su contraseña. Sin token se pinta con los valores por defecto, que es lo correcto:
    // en el login todavía no se sabe de qué clínica se trata.
    if (!token) {
      _memoria = null
      _memoriaToken = null
      fijarZonaDeLaClinica(null)
      fijarPaisTelefonoClinica(null)
      return () => { vivo = false }
    }

    // Ya se preguntó en esta sesión: `regional` ya lo está usando (se deriva arriba).
    if (_memoria && _memoriaToken === token) return () => { vivo = false }

    api.get('/regional')
      .then(({ data }) => {
        if (!vivo) return
        // Se acepta tanto `{ ...configuración }` como `{ regional: {...}, aviso }`: las dos formas
        // son razonables en la pieza C y no merece la pena que un contrato de envoltorio decida si
        // la app pinta bien o mal.
        const cuerpo = (data && typeof data === 'object' && data.regional) ? data.regional : data
        const limpio = _sanear(cuerpo)
        _memoria = limpio
        _memoriaToken = token
        fijarZonaDeLaClinica(limpio.zona_horaria)
        fijarPaisTelefonoClinica(limpio.pais)
        try { localStorage.setItem(CLAVE(), JSON.stringify(limpio)) } catch { /* almacenamiento lleno */ }
        setCargada({ token, cfg: limpio })
        setAviso(_aviso(cuerpo, data))
      })
      .catch(() => {
        if (!vivo) return
        // NI UNA PANTALLA EN BLANCO POR ESTO. Toda la app va a pasar por aquí: si la llamada falla
        // (red, despliegue a medias, migración sin aplicar), se sigue con los valores por defecto y
        // se pinta como se pintaba antes de esta tanda. Se deja constancia para Ajustes.
        setAviso(i18nT('context.RegionalContext.no_se_ha_podido_leer', 'No se ha podido leer la configuración regional. Se está usando la configuración por defecto.'))
      })

    return () => { vivo = false }
  }, [token, version])

  /**
   * Aplica en caliente lo que acaba de guardar Ajustes (pieza E), con lo que devuelve el PUT.
   * Repinta la app entera al instante y sin volver a preguntar: el usuario elige el formato y lo ve
   * cambiar, que es la única forma de saber que ha funcionado.
   */
  const aplicar = useCallback((nuevo) => {
    // Se le puede pasar la respuesta del PUT tal cual: sus avisos también valen (por ejemplo, que
    // cambiar la moneda no convierte ni un importe de los que ya están guardados).
    const cuerpo = (nuevo && typeof nuevo === 'object' && nuevo.regional) ? nuevo.regional : nuevo
    const limpio = _sanear(cuerpo)
    const t = _token()
    _memoria = limpio
    _memoriaToken = t
    fijarZonaDeLaClinica(limpio.zona_horaria)
    try { localStorage.setItem(CLAVE(), JSON.stringify(limpio)) } catch { /* almacenamiento lleno */ }
    setCargada({ token: t, cfg: limpio })
    setAviso(_aviso(cuerpo, nuevo))
    return limpio
  }, [])

  /** Vuelve a preguntar al servidor. */
  const refrescar = useCallback(() => { olvidarRegional() }, [])

  // El idioma de la clínica se le dice al módulo de textos. HOY no carga ningún diccionario (R1 deja
  // el castellano y solo el castellano), pero `t()` lo usa para las FORMAS DE PLURAL: es lo único
  // que ya depende del idioma antes de que exista una sola traducción. Cuando R3 traiga los
  // diccionarios, se cargan aquí y no hay que tocar ni una de las llamadas a `t()`.
  useEffect(() => { FMT.cargarTraducciones(regional.idioma, null) }, [regional.idioma])

  // Se recalculan solo cuando cambia la configuración: este proveedor envuelve la app entera y estas
  // funciones acaban en las dependencias de los useMemo de las pantallas. Si cambiaran de identidad
  // en cada pintada, arrastrarían recálculos por todos lados.
  const f = useMemo(() => _formateadores(regional), [regional])

  const valor = useMemo(() => ({
    regional,
    f,
    // `t` se ofrece aquí para que una pantalla tenga UN solo import, aunque hoy sea la misma función
    // pura de services/formatos.js. El día que haya diccionarios por idioma, cambia esto y no las
    // 8.000 llamadas.
    t: FMT.t,
    cargando,
    // Un aviso de una sesión anterior no puede sobrevivir al cierre de sesión: se leería como si le
    // pasara algo a la clínica que acaba de entrar.
    aviso: token ? aviso : null,
    aplicar,
    refrescar,
  }), [regional, f, cargando, aviso, token, aplicar, refrescar])

  return <RegionalContext.Provider value={valor}>{children}</RegionalContext.Provider>
}

// ── EL VALOR DE EMERGENCIA ──────────────────────────────────────────────────────────────────────
// `useRegional()` fuera del proveedor NO lanza, y es deliberado. Lo van a llamar cientos de
// pantallas, y algunas se pintan en árboles propios (el portal del paciente, los portales de
// profesionales externos, un modal montado por un portal de React durante una prueba). Lanzar ahí
// convierte "falta un proveedor" en una pantalla en blanco delante de un paciente. Con esto se pinta
// con los valores por defecto: exactamente igual que antes de esta tanda.
let _emergencia = null
function _valorDeEmergencia() {
  if (!_emergencia) {
    _emergencia = {
      regional: DEFECTO,
      f: _formateadores(DEFECTO),
      t: FMT.t,
      cargando: false,
      aviso: null,
      aplicar: () => DEFECTO,
      refrescar: () => {},
    }
  }
  return _emergencia
}

/** La configuración de la clínica y los formateadores atados a ella. */
export function useRegional() {
  return useContext(RegionalContext) || _valorDeEmergencia()
}

/** Atajo para el caso habitual: solo se quieren los formateadores. */
export function useFormatos() {
  return useRegional().f
}

/** Atajo para el texto. Hoy devuelve el castellano que se le pase como segundo argumento. */
export function useT() {
  return useRegional().t
}
